import h from "trader-hyperscript";
import * as style from "./../../styles/style.css.classes";

import {
  Field,
  Parsing,
  numberBox,
  numberBoxOptional,
  selectBox,
  syncRawAndParsing,
  textBox,
} from "trader.ts/ui.forms";
import { questiontype } from "../../sql/classy/types";
import { Source } from "trader.ts/types/source";
import { scheduleForCleanup } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import { radioField, radioFieldCheckboxRenderInputLike } from "./radiofield";
import { mandatoryValidation } from "../lib/validate";
import { checkboxesField } from "./checkboxesfield";

export type question = {
  label: string;
  questiontype: questiontype;
  text_answer: string | null;
  boolean_answer: boolean | null;
  number_answer: number | null;
  select_answer: string | null;
  multiselect_answers: string[] | null;
  ordering: number;
  mandatory: boolean;
  select_options: string[] | null;
};
export async function questionsField<EXTRA>(
  questions: (question & EXTRA)[],
): Promise<Field<(question & EXTRA)[]>> {
  type questionWithId = question & EXTRA;
  const perQuestion = await Promise.all(
    questions.map((q) => makeQuestionInput(q)),
  );

  const s: Source<Parsing<questionWithId[]>> = new Source({ tag: "initial" });

  recalcAll();

  function recalcAll() {
    const totalValue = perQuestion.reduce(
      (p: Parsing<questionWithId[]>, question) => {
        if (p.tag !== "parsed") {
          return p;
        } else {
          const fieldVal = question.s.get();
          if (fieldVal.tag === "parsed") {
            return {
              tag: "parsed" as const,
              parsed: p.parsed.concat(fieldVal.parsed),
            };
          } else {
            return fieldVal;
          }
        }
      },
      { tag: "parsed" as const, parsed: [] },
    );
    s.set(totalValue);
  }

  return {
    s,
    render: () => {
      for (const question of perQuestion) {
        scheduleForCleanup(question.s.observe(recalcAll));
      }
      return (
        <div className={style.questionnaire}>
          {perQuestion.map((q) => (
            <article>{q.render()}</article>
          ))}
        </div>
      );
    },
    cleanup: () => {},
  };
}

export function wrapField<INNER, OUTER>(
  field: Field<INNER>,
  wrap: (t: INNER) => OUTER,
  unwrap: (u: OUTER) => INNER,
): Field<OUTER> {
  function parse(
    val: Parsing<INNER>,
  ):
    | { tag: "parsed"; parsed: OUTER }
    | undefined
    | { tag: "err"; label: string | undefined } {
    if (val.tag === "parsed") {
      return {
        tag: "parsed",
        parsed: wrap(val.parsed),
      };
    } else if (val.tag === "initial" || val.tag === "loading") {
      return undefined;
    } else if (val.tag === "err") {
      return { tag: "err", label: val.label };
    } else {
      return checkAllCasesHandled(val);
    }
  }
  const parsingS = new Source(
    parse(field.s.get()) || { tag: "initial" as const },
  );

  return {
    ...field,
    s: parsingS,
    render: () => {
      syncRawAndParsing({
        rawS: field.s,
        parsingS: parsingS,
        parse: parse,
        parsedToRaw: function (parsed) {
          return {
            tag: "parsed" as const,
            parsed: unwrap(parsed),
          };
        },
      });
      return field.render();
    },
  };
}

async function makeQuestionInput<
  EXTRA,
  T extends {
    label: string;
    questiontype: questiontype;
    text_answer: string | null;
    boolean_answer: boolean | null;
    select_answer: string | null;
    number_answer: number | null;
    multiselect_answers: string[] | null;
    ordering: number;
    mandatory: boolean;
    select_options: string[] | null;
  } & EXTRA,
>(question: T): Promise<Field<T>> {
  const label = question.label + (question.mandatory === true ? " *" : "");

  if (question.questiontype === "boolean") {
    const values = [...[{ value: false }, { value: true }], { value: null }];
    const field = radioField({
      initial: values.find((v) => v.value === question.boolean_answer)!,
      values: [{ value: false }, { value: true }, { value: null }],
      show: (t) =>
        t.value === true
          ? "Ja"
          : t.value === false
          ? "Nee"
          : t.value === null
          ? ""
          : checkAllCasesHandled(t.value),
      render: (s, selected, onclick) => {
        const el = radioFieldCheckboxRenderInputLike(s, selected, onclick);
        if (s === "") {
          el.setAttribute("style", "display: none;");
        }
        return el;
      },
      label,
      containerClass: style.equal_columns,
    });
    return wrapField(
      field,
      (v) => ({
        ...question,
        boolean_answer: v.value,
      }),
      (q: T) => values.find((v) => v.value === q.boolean_answer)!,
    );
  } else if (question.questiontype === "text") {
    const field = await textBox({
      initialVal: question.text_answer || "",
      label,
      type: "textarea",
      validations: question.mandatory ? [mandatoryValidation] : [],
    });
    return wrapField(
      field,
      (str) => ({
        ...question,
        text_answer: str,
      }),
      (q: T) => q.text_answer || "",
    );
  } else if (question.questiontype === "number") {
    const field: Field<number | null> = question.mandatory
      ? await numberBox(question.number_answer || 0, { label, step: 0.1 })
      : await numberBoxOptional(question.number_answer, { label, step: 0.1 });
    return wrapField(
      field,
      (n) => ({
        ...question,
        number_answer: n,
      }),
      (q: T) => q.number_answer,
    );
  } else if (question.questiontype === "select") {
    const options = question.mandatory
      ? (question.select_options || []).map((opt) => ({ value: opt }))
      : [
          { value: null },
          ...(question.select_options || []).map((opt) => ({ value: opt })),
        ];
    function calcInitial(question: {
      mandatory: boolean;
      select_answer: null | string;
    }): (typeof options)[number] | undefined {
      return (
        options.find((opt) => opt.value === question.select_answer) ||
        (question.mandatory ? undefined : options[0])
      );
    }
    const field = await selectBox({
      values: options,
      initial: calcInitial(question),
      show: (t) => (t.value === null ? "  " : t.value),
      label,
    });

    return wrapField(
      field,
      (str) => ({
        ...question,
        select_answer: str?.value || null,
      }),
      (q: T) => calcInitial(q),
    );
  } else if (question.questiontype === "multiselect") {
    const options = question.select_options || [];
    const field = checkboxesField({
      initial: options.filter(
        (opt) => question.multiselect_answers?.some((ma) => ma === opt),
      ),
      values: options,
      label,
      show: (t) => t,
      addNoFilterOption: false,
      checkboxClass: style.input_like,
    });

    return wrapField(
      field,
      (v) => ({
        ...question,
        multiselect_answers: v,
      }),
      (q: T) =>
        options.filter(
          (opt) => q.multiselect_answers?.some((ma) => ma === opt),
        ),
    );
  } else {
    return checkAllCasesHandled(question.questiontype);
  }
}
